type CoordinatesLocationProps = {
	coordinates: string | [number, number] | undefined;
	mapboxAPIKey: string | undefined;
};

type StateCountryLocationProps = {
	state: string | undefined;
	country: string | undefined;
};

type ParseLocationToPlaceProps =
	| CoordinatesLocationProps
	| StateCountryLocationProps
	| (CoordinatesLocationProps & StateCountryLocationProps);

export const parseLocationToPlace = async (
	props: ParseLocationToPlaceProps
) => {
	let locationName: string | undefined;

	if ('coordinates' in props) {
		const { coordinates, mapboxAPIKey } = props;

		locationName = await parseCoordinatesToLocation({
			coordinates,
			mapboxAPIKey
		});
	}

	if (!locationName && 'country' in props) {
		const { state, country } = props;
		locationName = parseStateCountryToLocation({ state, country });
	}

	return locationName;
};

const parseCoordinatesToLocation = async ({
	coordinates: coordinatesInput,
	mapboxAPIKey
}: CoordinatesLocationProps) => {
	if (!mapboxAPIKey) return undefined;

	let coordinates = coordinatesInput;

	try {
		if (typeof coordinates === 'string') {
			const parsedCoordinates: CoordinatesLocationProps['coordinates'] =
				JSON.parse(coordinates);

			coordinates =
				Array.isArray(parsedCoordinates) &&
				parsedCoordinates.length === 2
					? parsedCoordinates
					: undefined;
		}
	} catch {
		throw new Error(`Failed to parse coordinates string: ${coordinates}`);
	}

	if (!coordinates) return undefined;

	const [lng, lat] = coordinates;

	const location = await reverseGeocode({
		lng,
		lat,
		mapboxAPIKey
	});

	return location;
};

type ReverseGeoCodeDataProps = {
	type: string;
	query: [number, number];
	features: Array<{
		id: string;
		type: string;
		place_type: Array<string>;
		relevance: number;
		properties: {
			mapbox_id: string;
			wikidata: string;
		};
		text: string;
		place_name: string;
		bbox: Array<number>;
		center: [number, number];
		geometry: {
			type: string;
			coordinates: [number, number];
		};
		context: Array<{
			id: string;
			mapbox_id: string;
			wikidata: string;
			text: string;
		}>;
	}>;
	attribution: string;
};

type ReverseGeocodeProps = Pick<CoordinatesLocationProps, 'mapboxAPIKey'> & {
	lng: number;
	lat: number;
};

const reverseGeocode = async ({
	lat,
	lng,
	mapboxAPIKey
}: ReverseGeocodeProps): Promise<string | undefined> => {
	try {
		const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=place,region&access_token=${mapboxAPIKey}`;

		const response = await fetch(url);
		const { features = [] }: ReverseGeoCodeDataProps =
			await response.json();

		const { place, region } = features.reduce<{
			place?: string;
			region?: string;
		}>((acc, feature) => {
			if (!acc.place && feature.place_type.includes('place')) {
				acc.place = feature.text;
			}
			if (!acc.region && feature.place_type.includes('region')) {
				acc.region = feature.text;
			}
			return acc;
		}, {});

		return place && region ? `${place}, ${region}` : undefined;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Reverse geocoding failed:', error);
		return undefined;
	}
};

const parseStateCountryToLocation = ({
	state,
	country
}: StateCountryLocationProps) => {
	return state && country ? `${state}, ${country}` : country;
};
